import {useEffect, useState} from 'react'
import {isAppInRunningTabBrowser} from './helper'

interface UserChoice {
  outcome: 'accepted' | 'dismissed'
  platform: string
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<UserChoice>
  prompt(): Promise<void>
}

interface A2HS {
  showA2HS: boolean | undefined
  prompt: BeforeInstallPromptEvent | false
  promptToInstall: () => Promise<void>
}

interface Props {
  onInstallation?: () => void // trigger once after installation
}

/**
 * {@link https://gist.github.com/rikukissa/cb291a4a82caa670d2e0547c520eae53}
 */

function useAddToHomescreenPrompt({onInstallation}: Props = {}): A2HS {
  const [isInstalled, setIsInstalled] = useState<boolean>(false)
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | false>(false)
  const [isAppInBrowser, setIsAppInBrowser] = useState<boolean>(false)
  const [showA2HS, setShowA2HS] = useState<boolean | undefined>(undefined)

  const promptToInstall = () => {
    if (prompt) {
      return prompt.prompt()
    }
    console.warn('Tried installing before browser sent "beforeinstallprompt" event')
    return Promise.resolve()
  }

  useEffect(() => {
    setIsAppInBrowser(isAppInRunningTabBrowser())

    // This is fired after 'load'. `e.preventDefault()` prevents the default chrome prompt.
    // We stash the event and then trigger `prompt()` method, manually triggering installation popup.
    const ready = (e: BeforeInstallPromptEvent) => {
      e.preventDefault()
      setPrompt(e)
    }
    window.addEventListener('beforeinstallprompt', ready as any)
    return () => {
      window.removeEventListener('beforeinstallprompt', ready as any)
    }
  }, [])

  useEffect(() => {
    function updatePWAInstallationStatus() {
      setIsInstalled(true)
      onInstallation && onInstallation()
    }
    // You can use the userChoice property to determine if the user installed your app from within your user interface.
    // But, if the user installs your PWA from the address bar or other browser component, userChoice won't help.
    // Instead, you should listen for the appinstalled event.
    // It is fired whenever your PWA is installed, no matter what mechanism is used to install your PWA.
    window.addEventListener('appinstalled', updatePWAInstallationStatus)
    return () => {
      window.removeEventListener('appinstalled', updatePWAInstallationStatus)
    }
  }, [])

  // exhaustive check covering all cases

  useEffect(() => {
    const showA2HS = !isInstalled && prompt && isAppInBrowser
    setShowA2HS(showA2HS)
  }, [isInstalled, prompt, isAppInBrowser])

  return {prompt, promptToInstall, showA2HS}
}

export default useAddToHomescreenPrompt
